import {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useRouteMatch} from 'react-router-dom';
import {uniqueId} from 'lodash-es';
import classNames from 'classnames';
import DropMenu, {DropMenuTrigger, DropMenuOptionObject} from '@/components/DropMenu';
import sdk from '@/sdk';
import Icon, {IconSize} from '@/components/Icon';
import {
    HelperBtnSvg,
    HelperBtnCloseSvg,
    HelperBtnCallUsSvg,
    CloseSideSvg,
    HelperContactUsSvg,
    CustomerFeedBackSvg,
} from '@/icons';
import * as store from '@/store';
import routers from '@/config/routers';
import {DOC_TYPE} from '@/constants/document/doc';
import {getKnowledgePageRouteMatchInfo} from '@/utils/urlUtils';
import {feLogger, reportClickHelpCenterItem} from '@/buried';
import styles from './index.module.scss';
import {IAiChatPanel} from '@/interface';

interface IgetHelperOptionResult {
    title?: string;
    url?: string;
}
interface IProps {
    isBaikeHome?: boolean;
    curDocument?: WizDocument;
    docReady?: boolean;
    curRepository?: Repository;
}

function HelperBtn(props: IProps) {
    const {
        curDocument,
        isBaikeHome,
        docReady = false,
        curRepository,
    } = props;
    const intl = useIntl();
    const [visible, setVisible] = useState(false);
    const [showOptions, setShowOptions] = useState<boolean>(false);
    // 版本页
    const isEditionPage = useRouteMatch({
        path: routers.Knowledge.docEditionPath,
        strict: true,
        sensitive: true,
    });
    const isAiKm = useRouteMatch({
        path: routers.aikm.path,
        strict: true,
        sensitive: true,
    });
    const [isHide, setIsHide] = useState(false); // 是否隐藏帮助中心

    const {isDocPage} = getKnowledgePageRouteMatchInfo();

    // 打开如流客服
    const handleCustomer = useCallback(() => {
        window.open('baidu://message?appid=2RqGz3R6CPxAHDnwDyjErg', '_self');
        feLogger(reportClickHelpCenterItem({
            name: '在线客服',
        }));
    }, []);
    // 联系我们弹窗
    const handleContactUs = useCallback(() => {
        setVisible(true);
    }, []);
    // 打开配置页面
    const handleJumpCustom = useCallback((url: string = '', title: string = '') => {
        return () => {
            url && window.open(url, '_blank');
            feLogger(reportClickHelpCenterItem({
                name: title,
            }));
        };
    }, []);

    const isKnowledgePage = useRouteMatch({
        path: [
            routers.Knowledge.homeTagPath,
            routers.Knowledge.squareDocPath,
            routers.Knowledge.settingPath,
            routers.Knowledge.path,
        ],
    });

    const helpOptionsMemo = useMemo(() => {
        const helpOptions: DropMenuOptionObject[] = [
            {
                value: 'group_no_name',
                children: [
                    // 隐藏右下角?的联系我们
                    // {
                    //     value: `${uniqueId('random_value')}`,
                    //     label: <div className={styles.customerFeedbackBox}>
                    //         <Icon
                    //             svg={HelperBtnCallUsSvg}
                    //             size={IconSize.LGX}
                    //             noSpace
                    //         />
                    //         <span className={styles.feedbackSpace}>联系我们</span>
                    //     </div>,
                    //     onClick: handleContactUs,
                    // },
                    {
                        value: `${uniqueId('random_value')}`,
                        label: <div className={styles.customerFeedbackBox}>
                            <Icon
                                svg={CustomerFeedBackSvg}
                                size={IconSize.LGX}
                                noSpace
                            />
                            <span className={styles.feedbackSpace}>在线客服</span>
                        </div>,
                        // label: '我要反馈',
                        onClick: handleCustomer,
                    },
                ],
            },
        ];
        return helpOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [curRepository, isKnowledgePage?.isExact]);
    const [helperCustomOptions, setHelperCustomOptions] = useState(helpOptionsMemo);

    useEffect(() => {
        async function getHelperOpt() {
            const getCustomOpt = (result: IgetHelperOptionResult[]) => {
                return result.map(ite => {
                    return {
                        label: ite.title,
                        value: `${uniqueId('random_value')}`,
                        onClick: handleJumpCustom(ite?.url, ite.title),
                    };
                });
            };
            try {
                const result = await sdk.as.getHelperOption();
                const customOpt = getCustomOpt(result);
                setHelperCustomOptions([...customOpt, ...helpOptionsMemo]);
            } catch (error) {
                console.log(error);
            }
        }
        if (docReady) {
            getHelperOpt();
        }
    }, [handleJumpCustom, helpOptionsMemo, docReady]);

    useEffect(() => {
        if (curDocument && isDocPage && curDocument?.type === DOC_TYPE.PPT) {
            // PPT 页面隐藏帮助中心
            setIsHide(true);
        } else if (isEditionPage) {
            setIsHide(true);
        } else if (isAiKm) {
            setIsHide(true);
        } else if (isHide === true) {
            // 其他页面，若帮助中心被隐藏，需要恢复
            setIsHide(false);
        }
    }, [curDocument, isAiKm, isDocPage, isEditionPage, isHide]);

    const handleClose = () => {
        setVisible(false);
    };

    // 点击弹窗外关闭
    const wrapper = useRef<HTMLDivElement>(null);
    const handleDocumentClick = useCallback((e: Event) => {
        if (!wrapper.current) {
            return;
        }
        /** 点击弹窗之内的，不关闭；点击弹窗之外的，关闭 */
        if (!wrapper.current.contains(e.target as Node)
                && wrapper.current !== e.target) {
            setVisible(false);
        }
    }, []);

    function ContactUs() {
        return (
            <div className={styles.dialogBox} ref={wrapper}>
                <div className={styles.closeBtn}>
                    <Icon svg={CloseSideSvg} onClick={handleClose} size={IconSize.Auto} />
                </div>
                <div className={styles.dialogContent}>
                    <Icon svg={HelperContactUsSvg} size={IconSize.Auto} />
                    <div className={'contentText'}>{intl.formatMessage({id: 'helperCenterContactUs'})}</div>
                </div>
                <div className={styles.dialogFooterText}>
                    {intl.formatMessage({id: 'helperCenterContactUsEmail'})}
                </div>
            </div>
        );
    }

    useEffect(() => {
        document.body.addEventListener('click', handleDocumentClick);
        return () => {
            document.body.removeEventListener('click', handleDocumentClick);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        const event = showOptions ? 'mouseover' : 'mouseout';
        const btnDom = document.querySelector<HTMLElement>('#dropHelp');
        if (!btnDom) {
            return;
        }
        const eventData = {
            view: window,
            bubbles: true,
            cancelable: true,
        };
        btnDom.dispatchEvent(new MouseEvent(event, eventData));
    }, [showOptions]);

    function handleChangeVisible(visible: boolean) {
        setShowOptions(visible);
    }

    if (isBaikeHome) {
        return null;
    }

    return (
        <>
            <div className={classNames(
                styles.container,
                isHide && styles.hide
            )}
            >
                {
                    visible && <ContactUs />
                }
                <DropMenu
                    menuPopperVisible={showOptions}
                    triggerType={DropMenuTrigger.Hover}
                    options={helperCustomOptions}
                    className={classNames(styles.dropMenuStyle, 'helpDropMenu')}
                    placement={'bottom-end'}
                    value={null}
                    onChangeMenuPopperVisible={handleChangeVisible}
                >
                    <Icon
                        id={'dropHelp'}
                        className={styles.helpIcon}
                        svg={showOptions ? HelperBtnCloseSvg : HelperBtnSvg}
                        size={IconSize.Auto}
                    />
                </DropMenu>
            </div>
        </>
    );
}

export default store.connect([
    store.KEYS.CUR_DOCUMENT,
    store.KEYS.IS_BAIKE_HOME,
    store.KEYS.DOC_READY,
    store.KEYS.CUR_REPOSITORY,
])(HelperBtn);
